import { COUNTRY_ID } from './country.constant';
/**
 * Checks whether a given address line refers to a P.O. Box.
 *
 * This function uses a regex pattern to detect common variations
 * of "P.O. Box", such as "PO Box", "Post Office Box", "P.O. Bin", etc.
 *
 * @param {string} line - The address line to check.
 * @return {boolean} Returns `true` if the line matches a P.O. Box pattern, otherwise `false`.
 *
 * @example
 * isPOBox("P.O. Box 123"); // true
 * isPOBox("123 Main St");  // false
 */
export const isPOBox = (line) => {
  const poBoxPattern = /^(((p[\s.]?[o\s][.]?)\s?)|(post\s?office\s?))((box|bin|b\.?)?\s?(num|number|#)?\s?\d+)/igm;
  return poBoxPattern.test(line);
};

/**
 * Validates a ZIP or postal code based on the country.
 *
 * For the United States, it expects 5 digits or 5+4 format (e.g., 12345 or 12345-6789).
 * For Canada, it expects a valid Canadian postal code format (e.g., A1A 1A1).
 *
 * @param {string} zip - The ZIP or postal code to validate.
 * @param {number} countryId - The country identifier (e.g., COUNTRY_ID.UNITED_STATES or COUNTRY_ID.CANADA).
 * @return {string|null} Returns an error message string if invalid, or `null` if valid.
 *
 * @example
 * validateZip("12345", COUNTRY_ID.UNITED_STATES); // null
 * validateZip("123", COUNTRY_ID.UNITED_STATES);  // "Invalid Zip Code"
 * validateZip("K1A 0B1", COUNTRY_ID.CANADA);      // null
 */
export const validateZip = (zip, countryId) => {
  const zipUS = /^[0-9]{5}(?:-[0-9]{4})?$/;
  const zipCA = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
  if (countryId === COUNTRY_ID.UNITED_STATES && !zipUS.test(zip)) return 'Invalid Zip Code';
  if (countryId === COUNTRY_ID.CANADA && !zipCA.test(zip)) return 'Invalid Postal Code';
  return null;
};

/**
 * Validates an address object and returns a map of field-specific error messages.
 * Checks for required fields and enforces specific format rules based on country.
 *
 * - Rejects PO Boxes for street address
 * - Validates U.S. ZIP code and Canadian postal code formats
 *
 * @function
 * @param {Object} address - The address object to validate.
 * @param {string} address.First - First name (required).
 * @param {string} address.Last - Last name (required).
 * @param {string} address.AddressLineOne - Street address (required, must not be a PO Box).
 * @param {string} address.City - City name (required).
 * @param {string} address.State - State or province (required).
 * @param {string} address.Zip - ZIP or postal code (required and validated based on country).
 * @param {string|number} address.CountryID - Country identifier (required).
 * @param {string} address.Phone - Phone number (required).
 * @return {Object} An object mapping field names to error messages. Empty if no validation errors.
 *
 * @example
 * const errors = validateAddress({
 *   First: 'John',
 *   Last: '',
 *   AddressLineOne: 'PO Box 123',
 *   City: 'LA',
 *   State: 'CA',
 *   Zip: '90001',
 *   CountryID: 1,
 *   Phone: '',
 * });
 * // Returns: { Last: 'Last Name Required', AddressLineOne: 'Please enter a valid street name for shipping address', Phone: 'Phone Number Required' }
 */
export const validateAddress = (address) => {
  const formErrors = {};
  const isEmpty = (value) => String(value).trim() === '';

  if (isEmpty(address.First)) formErrors.First = 'First Name Required';
  if (isEmpty(address.Last)) formErrors.Last = 'Last Name Required';
  if (isEmpty(address.CountryID)) formErrors.CountryID = 'Country Required';

  if (isEmpty(address.AddressLineOne)) {
    formErrors.AddressLineOne = 'Street Address Required';
  } else if (isPOBox(address.AddressLineOne)) {
    formErrors.AddressLineOne = 'Please enter a valid street name for shipping address';
  }

  if (isEmpty(address.City)) formErrors.City = 'City Required';
  if (isEmpty(address.State)) formErrors.State = 'State Required';

  if (isEmpty(address.Zip)) {
    formErrors.Zip = 'Zip/Postal Code Required';
  } else {
    const zipError = validateZip(address.Zip, Number(address.CountryID));
    if (zipError) formErrors.Zip = zipError;
  }

  if (isEmpty(address.Phone)) formErrors.Phone = 'Phone Number Required';

  return formErrors;
};
